import React from 'react'
import { navigate } from 'gatsby'
import { useForm, useStore } from '@tanstack/react-form'
import { Alert, Box, Button, Flex, Label, Input, Text } from 'theme-ui'
import { ExclamationTriangle } from '@emotion-icons/fa-solid'

import { siteMetadata } from 'config'
import { OutboundLink } from 'components/link'
import { hasWindow } from 'util/dom'
import { useAuth } from './Provider'

const { contactEmail } = siteMetadata

const LoginForm = () => {
  const { login } = useAuth()

  const queryParams = hasWindow
    ? new URLSearchParams(window.location.search)
    : null
  const redirPath = hasWindow ? queryParams.get('redir') || '/' : '/'
  const showMessage = hasWindow ? queryParams.get('showMessage') : false

  const form = useForm({
    defaultValues: {
      id: '',
      password: '',
    },
    validators: {
      onSubmitAsync: async ({ value: { id, password } }) => {
        if (id === '' || password === '') {
          return {
            form: 'missing data',
            fields: {
              id: id === '' ? 'username is required' : undefined,
              password: password === '' ? 'password is required' : undefined,
            },
          }
        }

        const response = await login(id, password)
        if (response.error) {
          if (response.error === 'INVALID_CREDENTIALS') {
            return {
              form: response.error,
              fields: {
                id: 'invalid username or password',
                password: 'invalid username or password',
              },
            }
          }

          return {
            form: response.error,
            fields: {
              id: undefined,
              password: undefined,
            },
          }
        }

        if (response.isLoggedIn) {
          navigate(redirPath)
        }

        return undefined
      },
    },
  })

  const errors = useStore(form.store, (state) => state.errors)

  return (
    <>
      {errors.length > 0 && errors[0] === 'UNHANDLED_ERROR' ? (
        <Alert
          sx={{ color: 'red', bg: '#FFF', border: '1px solid red', mb: '2rem' }}
        >
          <Box>
            We&apos;re sorry, there was an error trying to sign you again.
            Please try again in a few minutes.
            <br />
            <br />
            If that doesn&apos;t work, please{' '}
            <OutboundLink to={`mailto:${contactEmail}`} sx={{ color: 'red' }}>
              let us know
            </OutboundLink>
            .
          </Box>
        </Alert>
      ) : null}

      {showMessage ? (
        <Flex sx={{ gap: '1rem' }}>
          <Box sx={{ flex: '0 0 auto', color: 'highlight' }}>
            <ExclamationTriangle size="2rem" />
          </Box>
          <Box sx={{ flex: '1 1 auto', mb: '2rem' }}>
            You were redirected here from a page that requires you to log in.
            You will be redirected there once you log in.
          </Box>
        </Flex>
      ) : null}

      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <Box>
          <form.Field
            name="id"
            listeners={{
              onChange: () => {
                form.validateField('password', 'change')
              },
            }}
          >
            {(field) => (
              <>
                <Label
                  htmlFor={field.name}
                  sx={{
                    fontWeight: 'bold',
                    color: !field.state.meta.isValid ? 'red' : undefined,
                  }}
                >
                  Username
                </Label>
                <Input
                  name={field.name}
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  autoFocus
                  aria-required
                  aria-invalid={!field.state.meta.isValid}
                  sx={{
                    ...(!field.state.meta.isValid
                      ? {
                          borderColor: 'red',
                          boxShadow: '0px 0px 3px red',
                        }
                      : {}),
                  }}
                />

                <Text sx={{ color: 'red', mt: '0.25rem' }}>
                  {!field.state.meta.isValid ? (
                    `Error: ${field.state.meta.errors.join(', ')}`
                  ) : (
                    <br />
                  )}
                </Text>
              </>
            )}
          </form.Field>
        </Box>

        <Box sx={{ mt: '1rem' }}>
          <form.Field
            name="password"
            listeners={{
              onChange: () => {
                form.validateField('id', 'change')
              },
            }}
          >
            {(field) => (
              <>
                <Label
                  htmlFor={field.name}
                  sx={{
                    fontWeight: 'bold',
                    color: !field.state.meta.isValid ? 'red' : undefined,
                  }}
                >
                  Password
                </Label>
                <Input
                  type="password"
                  name={field.name}
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  aria-required
                  aria-invalid={!field.state.meta.isValid}
                  sx={{
                    ...(!field.state.meta.isValid
                      ? {
                          borderColor: 'red',
                          boxShadow: '0px 0px 3px red',
                        }
                      : {}),
                  }}
                />
                <Text sx={{ color: 'red', mt: '0.25rem' }}>
                  {!field.state.meta.isValid ? (
                    `Error: ${field.state.meta.errors.join(', ')}`
                  ) : (
                    <br />
                  )}
                </Text>
              </>
            )}
          </form.Field>
        </Box>

        <Flex sx={{ justifyContent: 'flex-end', mt: '1rem' }}>
          <Button type="submit" onClick={() => form.handleSubmit()}>
            Log in
          </Button>
        </Flex>
      </Box>
    </>
  )
}

export default LoginForm
